// Shared presentational pieces for playEngine and games agent tabs.

import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@material-ui/core";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import UndoIcon from "@material-ui/icons/Undo";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import BugReportIcon from "@material-ui/icons/BugReport";
import HistoryIcon from "@material-ui/icons/History";
import RestoreIcon from "@material-ui/icons/Restore";
import BookmarkBorderIcon from "@material-ui/icons/BookmarkBorder";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import IconButton from "@material-ui/core/IconButton";
import { readAgentBalances, getAgentCreditRenewalDate, formatAgentCreditDate } from "./agentPlanReset";

const PINNED_MESSAGE_ROWS_EXPANDED = 10;

export const CARD_BG = "#474747";
export const FIELD_BG = "#313131";
export const CONSOLE_BG = "#1a1a1a";

export const EVENT_COLORS = {
  start: "#7e57c2",
  manifest: "#ab47bc",
  turn: "#42a5f5",
  tool_result: "#26a69a",
  tool_error: "#ef5350",
  pause: "#ffb74d",
  handoff: "#ffcc80",
  handoff_error: "#ef5350",
  done: "#66bb6a",
  error: "#ef5350",
};

export function timeAgo(dateValue) {
  if (!dateValue) return "";
  const then = new Date(dateValue).getTime();
  if (isNaN(then)) return "";
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  return `${diffDay}d ago`;
}

export function formatExactDateTime(dateValue) {
  if (!dateValue) return "";
  const d = new Date(dateValue);
  if (isNaN(d.getTime())) return "";
  try {
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch (_err) {
    return d.toLocaleString();
  }
}

function ManifestEntry({ entry }) {
  const [open, setOpen] = useState(false);
  const color = EVENT_COLORS.manifest;
  const blocks = entry.blocks || [];
  const tools = entry.toolNames || [];

  return (
    <div style={{ borderLeft: `2px solid ${color}`, paddingLeft: "8px", marginBottom: "4px" }}>
      <div
        style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}
        onClick={() => setOpen((v) => !v)}
      >
        <Typography variant="caption" style={{ color, fontWeight: 700, minWidth: "80px", fontFamily: "monospace" }}>
          run context
        </Typography>
        <Typography variant="caption" style={{ color: "#888", fontFamily: "monospace" }}>
          {blocks.length} blocks · {tools.length} tools{entry.preflight ? " · preflight" : ""}
        </Typography>
        <span style={{ marginLeft: "auto", color: "#555" }}>
          {open ? <ExpandLessIcon style={{ fontSize: 14 }} /> : <ExpandMoreIcon style={{ fontSize: 14 }} />}
        </span>
      </div>

      <Collapse in={open}>
        {blocks.length > 0 && (
          <pre style={{
            fontSize: "11px", color: "#ccc", backgroundColor: "#111",
            padding: "6px 8px", borderRadius: "3px", marginTop: "4px",
            overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all",
            maxHeight: "200px", overflowY: "auto",
          }}>
            <span style={{ color: "#888", display: "block", marginBottom: "2px", fontSize: "10px" }}>PROMPT BLOCKS</span>
            {blocks.map((b) => `${b.name}  (${b.chars} chars)`).join("\n")}
          </pre>
        )}
        {tools.length > 0 && (
          <pre style={{
            fontSize: "11px", color: "#ccc", backgroundColor: "#111",
            padding: "6px 8px", borderRadius: "3px", marginTop: "4px",
            overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all",
            maxHeight: "200px", overflowY: "auto",
          }}>
            <span style={{ color: "#888", display: "block", marginBottom: "2px", fontSize: "10px" }}>TOOLS</span>
            {tools.join(", ")}
          </pre>
        )}
        {entry.systemPrompt && (
          <pre style={{
            fontSize: "11px", color: "#ccc", backgroundColor: "#111",
            padding: "6px 8px", borderRadius: "3px", marginTop: "4px",
            overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word",
            maxHeight: "400px", overflowY: "auto",
          }}>
            <span style={{ color: "#ab47bc", display: "block", marginBottom: "2px", fontSize: "10px" }}>SYSTEM PROMPT</span>
            {entry.systemPrompt}
          </pre>
        )}
      </Collapse>
    </div>
  );
}

export function LogEntry({ entry }) {
  const [open, setOpen] = useState(false);
  const color = EVENT_COLORS[entry.event] || "#aaa";

  if (entry.event === "manifest") return <ManifestEntry entry={entry} />;

  const hasDetail = entry.input || entry.result || entry.text || entry.tools_called || entry.error || entry.summary;

  return (
    <div style={{ borderLeft: `2px solid ${color}`, paddingLeft: "8px", marginBottom: "4px" }}>
      <div
        style={{ display: "flex", alignItems: "center", gap: "6px", cursor: hasDetail ? "pointer" : "default" }}
        onClick={() => hasDetail && setOpen((v) => !v)}
      >
        <Typography variant="caption" style={{ color, fontWeight: 700, minWidth: "80px", fontFamily: "monospace" }}>
          {entry.event}
        </Typography>
        {entry.turn && (
          <Typography variant="caption" style={{ color: "#888", fontFamily: "monospace" }}>
            turn {entry.turn}
          </Typography>
        )}
        {entry.tool && (
          <Typography variant="caption" style={{ color: "#ffa726", fontFamily: "monospace" }}>
            {entry.tool}
          </Typography>
        )}
        {entry.stop_reason && (
          <Typography variant="caption" style={{ color: "#888", fontFamily: "monospace" }}>
            › {entry.stop_reason}
          </Typography>
        )}
        {entry.note && (
          <Typography variant="caption" style={{ color: "#888", fontFamily: "monospace" }}>
            ({entry.note})
          </Typography>
        )}
        {entry.reason && (
          <Typography variant="caption" style={{ color: "#ffb74d", fontFamily: "monospace" }}>
            {entry.reason}
          </Typography>
        )}
        {entry.error && !open && (
          <Typography variant="caption" style={{ color: "#ef5350", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "200px" }}>
            {entry.error}
          </Typography>
        )}
        {hasDetail && (
          <span style={{ marginLeft: "auto", color: "#555" }}>
            {open ? <ExpandLessIcon style={{ fontSize: 14 }} /> : <ExpandMoreIcon style={{ fontSize: 14 }} />}
          </span>
        )}
      </div>

      <Collapse in={open}>
        {entry.input && (
          <pre style={{
            fontSize: "11px", color: "#ccc", backgroundColor: "#111",
            padding: "6px 8px", borderRadius: "3px", marginTop: "4px",
            overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all",
            maxHeight: "300px", overflowY: "auto",
          }}>
            <span style={{ color: "#888", display: "block", marginBottom: "2px", fontSize: "10px" }}>INPUT</span>
            {JSON.stringify(entry.input, null, 2)}
          </pre>
        )}
        {entry.result && (
          <pre style={{
            fontSize: "11px", color: "#ccc", backgroundColor: "#111",
            padding: "6px 8px", borderRadius: "3px", marginTop: "4px",
            overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all",
            maxHeight: "300px", overflowY: "auto",
          }}>
            <span style={{ color: "#26a69a", display: "block", marginBottom: "2px", fontSize: "10px" }}>RESPONSE</span>
            {JSON.stringify(entry.result, null, 2)}
          </pre>
        )}
        {!entry.input && !entry.result && (entry.text || entry.tools_called || entry.error || entry) && (
          <pre style={{
            fontSize: "11px", color: "#ccc", backgroundColor: "#6d4646",
            padding: "6px 8px", borderRadius: "3px", marginTop: "4px",
            overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all",
            maxHeight: "300px", overflowY: "auto",
          }}>
            {typeof entry.summary === "string"
              ? entry.summary
              : JSON.stringify(entry.text || entry.tools_called || entry.error || entry, null, 2)}
          </pre>
        )}
      </Collapse>
    </div>
  );
}

const AGENT_PROMPT_LABEL_STYLE = {
  color: "#aaa",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const AGENT_CREDIT_LABEL_STYLE = {
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.5px",
  whiteSpace: "nowrap",
};

const CLICKABLE_CREDIT_LABEL_STYLE = {
  cursor: "pointer",
};

function ClickableCreditLabel({ children, onClick, color, style }) {
  return (
    <Typography
      variant="caption"
      component="span"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        ...AGENT_CREDIT_LABEL_STYLE,
        ...CLICKABLE_CREDIT_LABEL_STYLE,
        color,
        ...style,
      }}
    >
      {children}
    </Typography>
  );
}

export function AgentCreditsInfoDialog({ open, onClose, userCompany }) {
  const balances = readAgentBalances(userCompany);
  const renewalDate = getAgentCreditRenewalDate(userCompany);
  const renewalLabel = formatAgentCreditDate(renewalDate);
  const showExtra = balances.extraCredit > 0;

  const rowStyle = {
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "10px",
    fontSize: "13px",
    color: "#e0e0e0",
  };

  const labelStyle = { color: "#aaa" };
  const valueStyle = { color: "#80cbc4", fontWeight: 600, fontFamily: "monospace" };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle style={{ color: "#fff", backgroundColor: "#474747" }}>
        Agent Credits
      </DialogTitle>
      <DialogContent style={{ backgroundColor: "#404040", color: "#e0e0e0" }}>
        <div style={rowStyle}>
          <span style={labelStyle}>Promotion Credits</span>
          <span style={valueStyle}>{balances.promotionCredit.toLocaleString()}</span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>Remaining Credits</span>
          <span style={valueStyle}>
            {balances.credit.toLocaleString()} / {balances.monthlyCredit.toLocaleString()}
          </span>
        </div>
        {showExtra && (
          <div style={rowStyle}>
            <span style={labelStyle}>Extra Credits</span>
            <span style={valueStyle}>{balances.extraCredit.toLocaleString()}</span>
          </div>
        )}
        <div style={{ ...rowStyle, marginBottom: "16px", paddingTop: "4px", borderTop: "1px solid #555" }}>
          <span style={labelStyle}>Total available</span>
          <span style={{ ...valueStyle, color: balances.totalSpendable > 0 ? "#80cbc4" : "#ef9a9a" }}>
            {balances.totalSpendable.toLocaleString()}
          </span>
        </div>
        {renewalLabel && (
          <DialogContentText style={{ color: "#bbb", fontSize: "12px", lineHeight: 1.6, marginTop: "12px" }}>
            Your monthly credits will be renewed at {renewalLabel}.
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions style={{ backgroundColor: "#474747" }}>
        <Button onClick={onClose} color="primary">
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function AgentCreditBalance({ userCompany }) {
  const [infoOpen, setInfoOpen] = useState(false);
  const openInfo = () => setInfoOpen(true);

  const {
    credit,
    extraCredit,
    promotionCredit,
    monthlyCredit,
    totalSpendable,
  } = readAgentBalances(userCompany);

  const color = totalSpendable > 0 ? "#80cbc4" : "#ef9a9a";
  const infoDialog = (
    <AgentCreditsInfoDialog
      open={infoOpen}
      onClose={() => setInfoOpen(false)}
      userCompany={userCompany}
    />
  );

  if (promotionCredit > 0) {
    return (
      <>
        <ClickableCreditLabel
          onClick={openInfo}
          color={color}
          style={{ textAlign: "center", transform: "translateX(160px)" }}
        >
          Promotion Credits: {promotionCredit.toLocaleString()}
        </ClickableCreditLabel>
        {infoDialog}
      </>
    );
  }

  const showExtra = extraCredit > 0;

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: showExtra ? "20px" : "0",
          width: showExtra ? "100%" : "auto",
        }}
      >
        <ClickableCreditLabel
          onClick={openInfo}
          color={color}
          style={{ marginRight: showExtra ? "16px" : 0 }}
        >
          Remaining Credits: {credit.toLocaleString()} / {monthlyCredit.toLocaleString()}
        </ClickableCreditLabel>
        {showExtra && (
          <ClickableCreditLabel onClick={openInfo} color={color}>
            Extra Credits: {extraCredit.toLocaleString()}
          </ClickableCreditLabel>
        )}
      </div>
      {infoDialog}
    </>
  );
}

export function AgentPromptHeader({ userCompany }) {
  const { promotionCredit } = readAgentBalances(userCompany);
  const showPromotion = promotionCredit > 0;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto 1fr",
        alignItems: "center",
        marginBottom: "10px",
        columnGap: "8px",
      }}
    >
      <Typography variant="caption" style={AGENT_PROMPT_LABEL_STYLE}>
        Agent Prompt
      </Typography>
      {showPromotion ? (
        <AgentCreditBalance userCompany={userCompany} />
      ) : (
        <div />
      )}
      {!showPromotion ? (
        <div style={{ justifySelf: "stretch", display: "flex", justifyContent: "flex-end" }}>
          <AgentCreditBalance userCompany={userCompany} />
        </div>
      ) : (
        <div />
      )}
    </div>
  );
}

export function AgentPinnedMessages({
  baseId,
  isAdmin,
  messages,
  onUpsert,
  onDelete,
  onAddDraft,
  createSnackbar,
}) {
  const [open, setOpen] = useState(false);
  const [savingKeys, setSavingKeys] = useState({});
  const [deleteConfirmEntry, setDeleteConfirmEntry] = useState(null);
  const [expandedFieldKeys, setExpandedFieldKeys] = useState({});
  const [editingKeys, setEditingKeys] = useState({});
  const [savedAdminByKey, setSavedAdminByKey] = useState({});

  useEffect(() => {
    setSavedAdminByKey((prev) => {
      const next = { ...prev };
      let changed = false;
      (messages || []).forEach((m) => {
        if (m._id && !editingKeys[m._localKey]) {
          const saved = m.isAdminMessage === true;
          if (next[m._localKey] !== saved) {
            next[m._localKey] = saved;
            changed = true;
          }
        }
      });
      return changed ? next : prev;
    });
  }, [messages, editingKeys]);

  if (!baseId) return null;

  const hasPinned = (messages || []).some((m) => m._id && m.message && String(m.message).trim());

  function startEditing(entry) {
    setEditingKeys((prev) => ({ ...prev, [entry._localKey]: true }));
  }

  async function handleSave(entry) {
    const trimmed = String(entry.message || "").trim();
    if (!trimmed) {
      if (createSnackbar) createSnackbar("Message cannot be empty", "error");
      return;
    }

    const key = entry._localKey;
    setSavingKeys((prev) => ({ ...prev, [key]: true }));
    try {
      await onUpsert({
        messageId: entry._id || null,
        message: trimmed,
        isAdminMessage: entry.isAdminMessage === true,
        _localKey: key,
      });
      setEditingKeys((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setExpandedFieldKeys((prev) => ({
        ...prev,
        [key]: false,
      }));
      setSavedAdminByKey((prev) => ({
        ...prev,
        [key]: entry.isAdminMessage === true,
      }));
    } catch (err) {
      if (createSnackbar) createSnackbar(err.message || "Could not save pinned message", "error");
    } finally {
      setSavingKeys((prev) => ({ ...prev, [key]: false }));
    }
  }

  async function handleRemove(entry) {
    const key = entry._localKey;
    if (entry._id) {
      setSavingKeys((prev) => ({ ...prev, [key]: true }));
      try {
        await onDelete(entry._id);
      } catch (err) {
        if (createSnackbar) createSnackbar(err.message || "Could not remove pinned message", "error");
      } finally {
        setSavingKeys((prev) => ({ ...prev, [key]: false }));
      }
      return;
    }
    onAddDraft && onAddDraft(null, { removeLocalKey: key });
  }

  return (
    <div style={{ marginBottom: "4px" }}>
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "6px 10px",
          backgroundColor: CONSOLE_BG,
          cursor: "pointer",
          userSelect: "none",
          borderRadius: open ? "4px 4px 0 0" : "4px",
        }}
      >
        <BookmarkBorderIcon style={{ fontSize: 15, color: "#ffb74d" }} />
        <Typography variant="caption" style={{ color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "11px", flexGrow: 1 }}>
          Pinned Message
          {hasPinned && (
            <span style={{ marginLeft: "8px", color: "#ffb74d" }}>active</span>
          )}
        </Typography>
        {open ? (
          <ExpandLessIcon style={{ fontSize: 16, color: "#888" }} />
        ) : (
          <ExpandMoreIcon style={{ fontSize: 16, color: "#888" }} />
        )}
      </div>

      <Collapse in={open}>
        <div
          style={{
            backgroundColor: CARD_BG,
            padding: "10px 12px",
            borderTop: "1px solid #333",
            borderRadius: "0 0 4px 4px",
          }}
        >
          <Typography variant="caption" style={{ color: "#aaa", display: "block", marginBottom: "8px" }}>
            Standing instructions for the agent on this base (included in every run).
          </Typography>

          {(messages || []).map((entry) => {
            const saving = Boolean(savingKeys[entry._localKey]);
            const isAdminMessage = entry.isAdminMessage === true;
            const savedAsAdmin = Boolean(entry._id && savedAdminByKey[entry._localKey]);
            const fieldExpanded = expandedFieldKeys[entry._localKey] ?? !entry._id;
            const isEditing = editingKeys[entry._localKey] ?? !entry._id;
            return (
              <div
                key={entry._localKey}
                style={{
                  marginBottom: "10px",
                  padding: "8px",
                  backgroundColor: FIELD_BG,
                  borderRadius: "4px",
                  border: savedAsAdmin ? "1px solid #ffb74d33" : "1px solid #333",
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: "4px" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ position: "relative" }}>
                      <TextField
                        multiline={fieldExpanded}
                        rows={fieldExpanded ? PINNED_MESSAGE_ROWS_EXPANDED : undefined}
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder="e.g. Always keep button captions short. Never change enemy damage values without asking."
                        value={entry.message || ""}
                        disabled={!isEditing || saving}
                        onChange={(e) => onAddDraft && onAddDraft(entry._localKey, { message: e.target.value })}
                        onKeyDown={(e) => e.stopPropagation()}
                        InputProps={{
                          style: {
                            color: isEditing ? "#fff" : "#bbb",
                            backgroundColor: isEditing ? CARD_BG : "#252525",
                            fontSize: "13px",
                            paddingRight: "36px",
                          },
                        }}
                        inputProps={{
                          style: fieldExpanded
                            ? { lineHeight: "1.6", overflowY: "auto" }
                            : { lineHeight: "1.6" },
                        }}
                      />
                      <IconButton
                        size="small"
                        disabled={saving}
                        title={fieldExpanded ? "Show one line" : "Show more lines"}
                        onClick={() => setExpandedFieldKeys((prev) => ({
                          ...prev,
                          [entry._localKey]: !fieldExpanded,
                        }))}
                        style={{
                          position: "absolute",
                          top: "6px",
                          right: "2px",
                          color: "#888",
                          padding: "4px",
                          width: "28px",
                          height: "28px",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "18px",
                            height: "18px",
                          }}
                        >
                          {fieldExpanded ? (
                            <ExpandLessIcon style={{ fontSize: 18 }} />
                          ) : (
                            <ExpandMoreIcon style={{ fontSize: 18 }} />
                          )}
                        </span>
                      </IconButton>
                    </div>
                    {fieldExpanded && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginTop: "6px",
                          gap: "8px",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                          {isAdmin && savedAsAdmin && (
                            <Typography
                              variant="caption"
                              style={{
                                color: "#ffb74d",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                flexShrink: 0,
                              }}
                            >
                              <SupervisorAccountIcon style={{ fontSize: 14 }} />
                              Admin
                            </Typography>
                          )}
                          {isAdmin && (
                            <FormControlLabel
                              style={{ marginLeft: 0, marginRight: 0 }}
                              control={(
                                <Checkbox
                                  size="small"
                                  color="primary"
                                  checked={isAdminMessage}
                                  disabled={!isEditing || saving}
                                  onChange={(e) => onAddDraft && onAddDraft(entry._localKey, { isAdminMessage: e.target.checked })}
                                />
                              )}
                              label={(
                                <Typography variant="caption" style={{ color: "#aaa" }}>
                                  Admin only
                                </Typography>
                              )}
                            />
                          )}
                        </div>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={saving}
                          onClick={() => (isEditing ? handleSave(entry) : startEditing(entry))}
                          style={{
                            color: "#81c784",
                            borderColor: "#81c784",
                            fontSize: "11px",
                            minWidth: "56px",
                            flexShrink: 0,
                          }}
                        >
                          {saving ? <CircularProgress size={14} style={{ color: "#81c784" }} /> : (isEditing ? "Save" : "Edit")}
                        </Button>
                      </div>
                    )}
                  </div>
                  <IconButton
                    size="small"
                    disabled={saving}
                    title="Remove pinned message"
                    onClick={() => setDeleteConfirmEntry(entry)}
                    style={{
                      color: "#ef9a9a",
                      padding: "4px",
                      width: "28px",
                      height: "28px",
                      marginTop: "6px",
                      flexShrink: 0,
                    }}
                  >
                    {saving ? <CircularProgress size={14} style={{ color: "#888" }} /> : <DeleteIcon style={{ fontSize: 16 }} />}
                  </IconButton>
                </div>
              </div>
            );
          })}

          <Button
            variant="outlined"
            fullWidth
            startIcon={<AddIcon />}
            onClick={() => onAddDraft && onAddDraft(null, { addNew: true })}
            style={{ marginTop: "4px", color: "#bbb", borderColor: "#555" }}
          >
            Add pinned message
          </Button>
        </div>
      </Collapse>

      <Dialog
        open={Boolean(deleteConfirmEntry)}
        onClose={() => setDeleteConfirmEntry(null)}
        PaperProps={{ style: { backgroundColor: CARD_BG, color: "#fff" } }}
      >
        <DialogTitle style={{ color: "#fff" }}>Remove pinned message?</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ color: "#ccc" }}>
            This pinned message will be removed from the base. The agent will no longer see these instructions on future runs.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmEntry(null)} style={{ color: "#ccc" }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              const entry = deleteConfirmEntry;
              setDeleteConfirmEntry(null);
              if (entry) handleRemove(entry);
            }}
            style={{ color: "#f44336" }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export function AgentPromptCard({
  prompt,
  onPromptChange,
  onRun,
  loading,
  remoteRunning,
  versionId,
  placeholder,
  runLabel,
  userCompany,
  runDisabled,
}) {
  const isDisabled = Boolean(runDisabled);
  return (
    <Card style={{ backgroundColor: CARD_BG }}>
      <CardContent>
        <AgentPromptHeader userCompany={userCompany} />

        <TextField
          multiline
          minRows={5}
          maxRows={12}
          fullWidth
          variant="outlined"
          placeholder={placeholder}
          value={prompt}
          onChange={onPromptChange}
          onKeyDown={(e) => e.stopPropagation()}
          disabled={isDisabled}
          InputProps={{
            style: {
              color: "#fff",
              backgroundColor: FIELD_BG,
              fontSize: "13px",
            },
          }}
          inputProps={{ style: { lineHeight: "1.6" } }}
        />

        <Button
          variant="contained"
          color="primary"
          fullWidth
          disabled={loading || remoteRunning || !prompt.trim() || !versionId || isDisabled}
          onClick={onRun}
          style={{ marginTop: "10px", height: "36px" }}
        >
          {loading ? <CircularProgress size={18} style={{ color: "#fff" }} /> : runLabel}
        </Button>
      </CardContent>
    </Card>
  );
}

export function AgentResponseCard({ response, canUndo, undoing, onUndo, undoDisabled }) {
  if (!response) return null;

  return (
    <Card style={{ backgroundColor: CARD_BG }}>
      <CardContent>
        <Typography
          variant="caption"
          style={{ color: "#aaa", display: "block", marginBottom: "8px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}
        >
          {response.error ? "Error" : "Summary"}
        </Typography>
        <Typography
          variant="body2"
          style={{
            color: response.error ? "#f44336" : "#e0e0e0",
            whiteSpace: "pre-wrap",
            lineHeight: "1.7",
            fontSize: "13px",
            backgroundColor: FIELD_BG,
            padding: "10px 12px",
            borderRadius: "4px",
          }}
        >
          {response.error ?? response.summary}
        </Typography>

        {response.creditCost != null && (
          <Typography variant="caption" style={{ color: "#80cbc4", display: "block", marginTop: "6px", fontSize: "12px", fontFamily: "monospace" }}>
            This run: {response.creditCost.toLocaleString()} AI credits
            {response.balanceAfter != null && ` · ${Math.max(0, response.balanceAfter).toLocaleString()} left`}
          </Typography>
        )}

        {response.error && response.restored && (
          <Typography variant="caption" style={{ color: "#ffb74d", display: "block", marginTop: "6px", fontSize: "12px" }}>
            Restored to the previous version.
          </Typography>
        )}

        {!response.error && canUndo && (
          <Button
            variant="outlined"
            size="small"
            startIcon={undoing ? <CircularProgress size={14} style={{ color: "#fff" }} /> : <UndoIcon style={{ fontSize: 16 }} />}
            disabled={undoing || Boolean(undoDisabled)}
            onClick={onUndo}
            style={{ marginTop: "10px", color: "#fff", borderColor: "#777" }}
          >
            {undoing ? "Undoing…" : "Undo last change"}
          </Button>
        )}

        {response.changes && response.changes.length > 0 && (
          <div style={{ marginTop: "10px" }}>
            <Typography
              variant="caption"
              style={{ color: "#aaa", display: "block", marginBottom: "6px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}
            >
              Changes made ({response.changes.length})
            </Typography>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {response.changes.map((change, i) => (
                <div
                  key={i}
                  style={{
                    backgroundColor: FIELD_BG,
                    borderRadius: "4px",
                    padding: "6px 10px",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "8px",
                  }}
                >
                  <CheckCircleOutlineIcon style={{ color: "#4caf50", fontSize: "16px", marginTop: "1px", flexShrink: 0 }} />
                  <Typography variant="body2" style={{ color: "#ccc", fontSize: "12px", lineHeight: "1.5" }}>
                    <span style={{ color: "#90caf9", fontWeight: 600 }}>{change.tool}</span>
                    {change.input && change.input.componentId && ` — ${change.input.componentId}`}
                    {change.input && change.input.dependencyKey && ` › ${change.input.dependencyKey}`}
                    {change.input && change.input.path && ` › ${change.input.path}`}
                    {change.input && change.input.versionId && ` — ${change.input.versionId}`}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function AgentVersionHistory({
  versionId,
  checkpoints,
  historyOpen,
  onToggleHistory,
  restoringId,
  loading,
  remoteRunning,
  onRequestRestore,
  restoreDisabled,
}) {
  if (!versionId) return null;

  return (
    <div>
      <Collapse in={historyOpen}>
        <div
          style={{
            backgroundColor: CONSOLE_BG,
            padding: "8px 10px",
            maxHeight: "260px",
            overflowY: "auto",
            borderTop: "1px solid #333",
          }}
        >
          {checkpoints.length === 0 ? (
            <Typography variant="caption" style={{ color: "#555", fontFamily: "monospace" }}>
              No checkpoints yet.
            </Typography>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {checkpoints.map((cp) => {
                const isRestorePoint = cp.source === "restore";
                const label = isRestorePoint
                  ? "Restore point (pre-restore snapshot)"
                  : (cp.prompt || "Agent run");
                const isThisRestoring = restoringId === cp.checkpointId;
                return (
                  <div
                    key={cp.checkpointId}
                    style={{
                      backgroundColor: FIELD_BG,
                      borderRadius: "4px",
                      padding: "6px 10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div style={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        style={{
                          color: isRestorePoint ? "#90a4ae" : "#ccc",
                          fontSize: "12px",
                          lineHeight: "1.4",
                          fontStyle: isRestorePoint ? "italic" : "normal",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={label}
                      >
                        {label}
                      </Typography>
                      <Typography
                        variant="caption"
                        style={{ color: "#777", fontSize: "11px" }}
                        title={formatExactDateTime(cp.dateCreated)}
                      >
                        {formatExactDateTime(cp.dateCreated)}
                        {timeAgo(cp.dateCreated) && (
                          <span style={{ color: "#555", marginLeft: "6px" }}>
                            ({timeAgo(cp.dateCreated)})
                          </span>
                        )}
                      </Typography>
                    </div>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={isThisRestoring
                        ? <CircularProgress size={12} style={{ color: "#fff" }} />
                        : <RestoreIcon style={{ fontSize: 15 }} />}
                      disabled={Boolean(restoringId) || loading || remoteRunning || Boolean(restoreDisabled)}
                      onClick={() => onRequestRestore(cp)}
                      style={{ color: "#fff", borderColor: "#777", flexShrink: 0, minWidth: "92px" }}
                    >
                      {isThisRestoring ? "Restoring…" : "Restore"}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Collapse>

      <Divider style={{ backgroundColor: "#555", marginBottom: "0" }} />
      <div
        onClick={onToggleHistory}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "6px 10px",
          backgroundColor: CONSOLE_BG,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <HistoryIcon style={{ fontSize: 15, color: "#4db6ac" }} />
        <Typography variant="caption" style={{ color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "11px", flexGrow: 1 }}>
          Version History
          {checkpoints.length > 0 && (
            <span style={{ marginLeft: "8px", color: "#4db6ac" }}>{checkpoints.length}</span>
          )}
        </Typography>
        {historyOpen ? (
          <ExpandMoreIcon style={{ fontSize: 16, color: "#888" }} />
        ) : (
          <ExpandLessIcon style={{ fontSize: 16, color: "#888" }} />
        )}
      </div>
    </div>
  );
}

export function AgentAdminConsole({
  isAdmin,
  consoleOpen,
  onToggleConsole,
  debugLog,
  loading,
  providerName,
  agentModel,
  tokenUsage,
  usdCost,
  pricingExplanation,
}) {
  if (!isAdmin) return null;

  return (
    <div>
      <Divider style={{ backgroundColor: "#555", marginBottom: "0" }} />

      <div
        onClick={onToggleConsole}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "6px 10px",
          backgroundColor: CONSOLE_BG,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <BugReportIcon style={{ fontSize: 15, color: "#7e57c2" }} />
        <Typography variant="caption" style={{ color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "11px", flexGrow: 1 }}>
          Admin Console
          {debugLog && debugLog.length > 0 && (
            <span style={{ marginLeft: "8px", color: "#7e57c2" }}>{debugLog.length} events</span>
          )}
          {providerName && agentModel && (
            <span style={{ marginLeft: "8px", color: "#4db6ac", fontFamily: "monospace", textTransform: "none", fontWeight: 400 }}>
              {providerName} / {agentModel}
            </span>
          )}
          {tokenUsage && (
            <span style={{ marginLeft: "8px", color: "#546e7a", fontFamily: "monospace" }}>
              ↑{tokenUsage.input.toLocaleString()} ↓{tokenUsage.output.toLocaleString()} tokens
              {usdCost != null && usdCost > 0 && (
                <span style={{ marginLeft: "6px", color: "#78909c" }}>(${usdCost.toFixed(6)})</span>
              )}
            </span>
          )}
        </Typography>
        {consoleOpen ? (
          <ExpandLessIcon style={{ fontSize: 16, color: "#888" }} />
        ) : (
          <ExpandMoreIcon style={{ fontSize: 16, color: "#888" }} />
        )}
      </div>

      <Collapse in={consoleOpen}>
        <div
          style={{
            backgroundColor: CONSOLE_BG,
            padding: "8px 10px",
            maxHeight: "340px",
            overflowY: "auto",
            borderTop: "1px solid #333",
          }}
        >
          {pricingExplanation && (
            <Typography
              variant="caption"
              style={{
                color: "#a5d6a7",
                display: "block",
                marginBottom: "10px",
                fontSize: "11px",
                lineHeight: "1.6",
                fontFamily: "monospace",
                backgroundColor: "#1e2a1e",
                border: "1px solid #2e5a30",
                borderRadius: "4px",
                padding: "8px 10px",
              }}
            >
              {pricingExplanation}
            </Typography>
          )}
          {!debugLog || debugLog.length === 0 ? (
            <Typography variant="caption" style={{ color: "#555", fontFamily: "monospace" }}>
              {loading ? "Agent running…" : "No log yet — run the agent to see workflow events."}
            </Typography>
          ) : (
            debugLog.map((entry, i) => <LogEntry key={i} entry={entry} />)
          )}
        </div>
      </Collapse>
    </div>
  );
}

export function AgentRestoreConfirmDialog({ open, onCancel, onConfirm }) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      PaperProps={{ style: { backgroundColor: CARD_BG, color: "#fff" } }}
    >
      <DialogTitle style={{ color: "#fff" }}>Are you sure?</DialogTitle>
      <DialogContent>
        <DialogContentText style={{ color: "#ccc" }}>
          This will replace the current version with the selected checkpoint. Your current
          state is snapshotted first, so you can undo this restore.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} style={{ color: "#ccc" }}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" color="primary" autoFocus>
          Restore
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Shown when a run pauses at its turn limit. The user sees the agent's handoff
// note (its own progress report) and decides: continue with a fresh turn
// budget, stop and keep the applied changes, or stop and undo this segment.
export function AgentContinueDialog({ open, info, onContinue, onKeep, onUndo, undoDisabled }) {
  if (!info) return null;
  const capReached = info.canContinue === false;
  const turnsLabel = info.cumulativeTurns != null ? ` It has used ${info.cumulativeTurns} turns so far.` : "";

  return (
    <Dialog
      open={open}
      onClose={onKeep}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { backgroundColor: CARD_BG, color: "#fff" } }}
    >
      <DialogTitle style={{ color: "#fff" }}>
        {capReached ? "Agent reached its turn limit" : "Agent is working longer than expected"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText style={{ color: "#ccc" }}>
          {capReached
            ? `The agent hit the maximum number of turns for a single task and stopped.${turnsLabel} The changes it made are applied — review its progress below, then keep or undo them.`
            : `The task is not finished yet.${turnsLabel} Its progress so far is summarized below. Continue working on it?`}
        </DialogContentText>
        {info.summary && (
          <div
            style={{
              backgroundColor: FIELD_BG,
              borderRadius: "4px",
              padding: "10px 12px",
              maxHeight: "220px",
              overflowY: "auto",
              marginTop: "8px",
            }}
          >
            <Typography variant="body2" style={{ color: "#e0e0e0", whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.6" }}>
              {info.summary}
            </Typography>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onUndo} disabled={Boolean(undoDisabled)} style={{ color: "#ef9a9a" }}>
          Stop &amp; undo
        </Button>
        <Button onClick={onKeep} style={{ color: "#ccc" }}>
          {capReached ? "Keep changes" : "Stop & keep changes"}
        </Button>
        {!capReached && (
          <Button onClick={onContinue} variant="contained" color="primary" autoFocus>
            Continue
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export function AgentRunningBanner({ loading, remoteRunning }) {
  if (!loading && !remoteRunning) return null;

  return (
    <div style={{ backgroundColor: "#1a2233", border: "1px solid #1565c0", borderRadius: "6px", padding: "8px 12px" }}>
      <Typography variant="caption" style={{ color: "#90caf9", fontWeight: 700, display: "block", marginBottom: "2px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        Agent Running
      </Typography>
      <Typography variant="caption" style={{ color: "#bbdefb", lineHeight: "1.5" }}>
        {remoteRunning
          ? "Another session is running the agent on this version. Waiting for it to finish…"
          : "Agent run in progress. You can leave this tab — the server will keep running and this page will update when it finishes."}
      </Typography>
    </div>
  );
}
